import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { BrandMark } from "../components/BrandLogo";

import { MessageSquareText, Bot, ShieldCheck, Loader2, CheckCheck } from "lucide-react";

const GOOGLE_ENABLED = process.env.REACT_APP_ENABLE_EMERGENT_AUTH === "true";

const inputCls =
  "w-full bg-white border border-[#CBD5E1] focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20 text-[#111B21] rounded-xl px-3.5 py-2.5 text-sm placeholder:text-[#8696A0] focus:outline-none transition-all";

function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { user, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [forgot, setForgot] = useState(false);
  const [fpStep, setFpStep] = useState("request");
  const [fpCode, setFpCode] = useState("");
  const [fpNew, setFpNew] = useState("");
  const [fpBusy, setFpBusy] = useState(false);

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    if (!email) return toast.error("Enter your account email");
    setFpBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If an account exists, a 6-digit code was emailed to you.");
      setFpStep("verify");
    } catch { toast.error("Could not send code. Try again."); }
    finally { setFpBusy(false); }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    if (fpNew.length < 6) return toast.error("Password must be at least 6 characters");
    setFpBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code: fpCode, new_password: fpNew });
      toast.success("Password reset! Please sign in with your new password.");
      setForgot(false); setFpStep("request"); setPassword(""); setFpCode(""); setFpNew("");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Reset failed");
    } finally { setFpBusy(false); }
  };

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Authentication failed");
    } finally { setBusy(false); }
  };

  // Google sign-in goes through Emergent's auth service, which only exists while hosted on Emergent.
  // Self-hosted builds hide it unless REACT_APP_ENABLE_EMERGENT_AUTH=true.
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F0F2F5]">
      {/* Left visual — WhatsApp teal panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#075E54] p-12">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(#ffffff 1.4px, transparent 1.4px)", backgroundSize: "26px 26px" }}
        />
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#128C7E]/40 blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-80 w-80 rounded-full bg-[#25D366]/20 blur-3xl" />

        <Link to="/" className="relative z-10 flex items-center gap-3 group" data-testid="login-logo-home-link">
          <BrandMark size={40} className="transition-transform group-hover:scale-105" />
          <span className="font-heading text-xl font-extrabold tracking-tight text-white">MuviArts<span className="opacity-70 font-semibold">.Online</span></span>
        </Link>

        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Chat with your<br />customers on WhatsApp.
          </h1>
          <p className="text-sm text-white/70 max-w-md leading-relaxed">
            Connect your real WhatsApp Business number, let AI reply instantly, and take over any conversation from one bright, familiar inbox.
          </p>
          <div className="flex items-center gap-2 text-[#D9FDD3] text-xs font-medium">
            <CheckCheck className="h-4 w-4" />
            Official Meta WhatsApp Cloud API · AI Chatbot · Lead Intelligence
          </div>
        </div>

        <div className="relative z-10 text-white/40 text-xs">© {new Date().getFullYear()} MuviArts.Online</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-7 animate-fade-up">
          <Link to="/" className="lg:hidden flex items-center gap-2.5" data-testid="login-logo-home-link-mobile">
            <BrandMark size={36} />
            <span className="font-heading text-lg font-extrabold text-[#111B21]">MuviArts<span className="opacity-60 font-semibold">.Online</span></span>
          </Link>

          <div className="space-y-1.5">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#111B21]">
              {forgot ? "Reset your password" : (mode === "login" ? "Welcome back" : "Create your account")}
            </h2>
            <p className="text-sm text-[#54656F]">
              {forgot ? "We'll email you a 6-digit code to reset your password." : (mode === "login" ? (GOOGLE_ENABLED ? "Sign in with your email & password or Google." : "Sign in with your email & password.") : "Get your own AI WhatsApp workspace in seconds.")}
            </p>
          </div>

          {forgot && (
            <div className="space-y-4" data-testid="forgot-panel">
              {fpStep === "request" ? (
                <form onSubmit={sendCode} className="space-y-3">
                  <input data-testid="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your account email" required className={inputCls} />
                  <button data-testid="fp-send-code" type="submit" disabled={fpBusy}
                    className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-xl font-semibold px-4 py-2.5 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50">
                    {fpBusy && <Loader2 className="h-4 w-4 animate-spin" />}Send reset code
                  </button>
                </form>
              ) : (
                <form onSubmit={resetPw} className="space-y-3">
                  <p className="text-xs text-[#54656F]">Enter the 6-digit code sent to <span className="font-semibold text-[#111B21]">{email}</span> and choose a new password.</p>
                  <input data-testid="fp-code" value={fpCode} onChange={(e) => setFpCode(e.target.value)}
                    placeholder="6-digit code" required className={inputCls} />
                  <input data-testid="fp-new-password" type="password" value={fpNew} onChange={(e) => setFpNew(e.target.value)}
                    placeholder="New password (min 6 chars)" required className={inputCls} />
                  <button data-testid="fp-reset-submit" type="submit" disabled={fpBusy}
                    className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-xl font-semibold px-4 py-2.5 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50">
                    {fpBusy && <Loader2 className="h-4 w-4 animate-spin" />}Reset password
                  </button>
                  <button type="button" data-testid="fp-resend" onClick={sendCode}
                    className="w-full text-center text-xs text-[#075E54] hover:underline">Didn't get it? Resend code</button>
                </form>
              )}
              <button type="button" data-testid="fp-back" onClick={() => { setForgot(false); setFpStep("request"); }}
                className="w-full text-center text-xs text-[#8696A0] hover:text-[#54656F]">← Back to sign in</button>
            </div>
          )}

          {!forgot && (<>
          <div className="flex gap-1 border border-[#E9EDEF] rounded-xl p-1 bg-[#F0F2F5]">
            <button data-testid="tab-login" onClick={() => setMode("login")}
              className={`flex-1 text-sm py-2 rounded-lg transition-colors ${mode === "login" ? "bg-white text-[#111B21] font-semibold shadow-sm" : "text-[#8696A0] hover:text-[#54656F]"}`}>Sign In</button>
            <button data-testid="tab-register" onClick={() => setMode("register")}
              className={`flex-1 text-sm py-2 rounded-lg transition-colors ${mode === "register" ? "bg-white text-[#111B21] font-semibold shadow-sm" : "text-[#8696A0] hover:text-[#54656F]"}`}>Sign Up</button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Full name" required className={inputCls} />
            )}
            <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email" required className={inputCls} />
            <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" required className={inputCls} />
            <button data-testid="auth-submit" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-xl font-semibold px-4 py-2.5 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </button>
            {mode === "login" && (
              <button type="button" data-testid="forgot-password-link" onClick={() => { setForgot(true); setFpStep("request"); }}
                className="w-full text-center text-xs text-[#075E54] hover:underline pt-1">Forgot password?</button>
            )}
          </form>

          {GOOGLE_ENABLED && (<>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E9EDEF]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#8696A0]">or</span>
            <div className="flex-1 h-px bg-[#E9EDEF]" />
          </div>

          <button
            data-testid="google-login-button"
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white text-[#111B21] border border-[#CBD5E1] hover:bg-[#F0F2F5] rounded-xl font-medium px-4 py-2.5 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>
          </>)}

          <div className="grid grid-cols-1 gap-2.5 pt-2">
            {[
              { icon: MessageSquareText, t: "Real WhatsApp Cloud API", d: "Official Meta integration" },
              { icon: Bot, t: "AI chatbot + knowledge base", d: "RAG-powered replies" },
              { icon: ShieldCheck, t: "Encrypted credentials", d: "Secrets never exposed" },
            ].map((f) => (
              <div key={f.t} className="flex items-start gap-3 border border-[#E9EDEF] rounded-xl p-3 bg-white">
                <span className="h-8 w-8 rounded-lg bg-[#E7FCE8] flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-4 w-4 text-[#075E54]" />
                </span>
                <div>
                  <p className="text-sm font-medium text-[#111B21]">{f.t}</p>
                  <p className="text-xs text-[#8696A0]">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
